import React, { useEffect, useState } from "react"
import axiosInstance from "../../libs/axios"
import { getAttendanceLeaveTypeLabel } from "../../libs/attendance"

interface LeaveQuota {
    leave_type: string
    quota: number | null
    period: "monthly" | "yearly"
    period_label: string
    block_over_quota: boolean
    used: number
    balance: number | null
    already_leave_day: boolean
    over_quota: boolean
    blocked: boolean
}

interface LeaveQuotaHintProps {
    userId?: number | string | null
    /** YYYY-MM-DD — the quota period is the one this day falls in. */
    date?: string | null
    leaveType: string
    /** Told whether a Leave day of `leaveType` is refused on `date`. */
    onBlockedChange?: (blocked: boolean) => void
}

/**
 * The employee's leave quota, shown under the Leave Type dropdown of the
 * attendance modals: allowed, used and left for the selected type in the
 * period the attendance date falls in. When the type is over a quota that
 * blocks (Leave Quota settings), it says so and the API refuses the mark.
 */
export const LeaveQuotaHint: React.FC<LeaveQuotaHintProps> = ({ userId, date, leaveType, onBlockedChange }) => {
    const [quotas, setQuotas] = useState<LeaveQuota[] | null>(null)
    const day = date ? String(date).slice(0, 10) : ""

    useEffect(() => {
        let cancelled = false
        setQuotas(null)
        if (!userId || !day) return
        axiosInstance
            .get("/attendance/leave-quota", { params: { user_id: userId, attendance_date: day } })
            .then((response) => {
                if (!cancelled) setQuotas(response.data?.data?.quotas || [])
            })
            .catch(() => {
                // The modal still works without the hint; the API enforces the quota.
                if (!cancelled) setQuotas([])
            })
        return () => {
            cancelled = true
        }
    }, [userId, day])

    const quota = quotas?.find((item) => item.leave_type === leaveType)
    const blocked = Boolean(quota?.blocked)

    useEffect(() => {
        onBlockedChange?.(blocked)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocked])

    if (!quota) return null
    const label = getAttendanceLeaveTypeLabel(quota.leave_type)

    if (quota.quota === null) {
        return (
            <p className="mt-1 text-xs text-gray-500">
                {label} leave has no fixed quota — {quota.used} used in {quota.period_label}.
            </p>
        )
    }

    return (
        <div
            className={`mt-2 rounded border px-3 py-2 text-xs ${quota.over_quota
                ? blocked
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                : "border-blue-100 bg-blue-50 text-blue-800"
                }`}
        >
            <div className="font-medium">
                {label} leave quota ({quota.period_label}): {quota.quota} allowed · {quota.used} used ·{" "}
                {Math.max(0, quota.balance ?? 0)} left
            </div>
            {quota.over_quota && (
                <div className="mt-0.5">
                    {blocked
                        ? "Quota exceeded — this day cannot be marked as this leave type."
                        : "Quota exceeded — this leave type allows marking beyond its quota."}
                </div>
            )}
        </div>
    )
}

export default LeaveQuotaHint
